import { useEffect, useState } from "react";
import BaseModal from "./BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import {
  serviceboxSyncApi,
  type ServiceBoxInterventionsPlan,
  type ServiceBoxPushInterventionsResult,
  type CarvisWorkLine,
} from "@services/api/serviceboxSync";
import { showNotification } from "context/notification.service";
import type { Ordre } from "types/ordre";
import { useDataStore } from "@store/user_org";
import {
  ALPHA_UNCONFIRMED_MESSAGE,
  alphaPosteFor,
  startAlphaTransfer,
  useAlphaTransferStore,
} from "@services/serviceboxAlphaTransfer";
import { serviceboxRdvId } from "@utils/serviceboxTransfer";

/**
 * The single "Mettre à jour ServiceBox" screen for a rendez-vous ServiceBox already holds.
 *
 * Carvis pushes to ServiceBox through three different mechanisms, for three different
 * reasons, and the garage used to get one button per mechanism. From the réceptionnaire's
 * chair there is only one intention — "j'ai changé des choses, envoie" — so they all live
 * here, in the order they have to run:
 *
 *   1. slots and desks — no button at all: a Carvis edit queues its own push
 *      (sb_rdv_push). Shown so the screen can say so rather than stay silent.
 *   2. interventions — ticked by hand, and never pre-ticked: the two sides word the same
 *      work differently ("REMPLACEMENT DES DISQUES ET PLAQUETTES DE FREIN ARRIÈRE" vs
 *      "DISQUES PLAQUETTES AR A REMPLACER"), and a pre-ticked suggestion shipped a
 *      duplicate on its first real use (22/09/2026). Matching only avoids the obvious one.
 *   3. the numéro d'OR — generated on demand (Alpha transfer on Carvis's poste 010, whose
 *      CT prints to PDF on the Alpha server since 23/09/2026, so no paper), or read back
 *      when the garage transferred from ServiceBox. Impossible for a MyO RDV with no
 *      ServiceBox dossier ("rdv:<id>"): only "Ouvrir dans ServiceBox" is offered there.
 *      Still last: pushing lines after an OR exists is too late for Alpha.
 *
 * Deliberately NOT the transfer modal, which *creates* a rendez-vous: run on an ordre
 * ServiceBox already has — almost all of them, since they are imported — it asks for a
 * second RDV on a column the first one occupies ("CCS indisponible").
 */
export default function ServiceBoxUpdateModal({
  ordre,
  onClose,
  onOrdreUpdated,
}: {
  ordre: Ordre;
  onClose: () => void;
  /** The numéro d'OR read here, for a host whose `ordre` is not store-fed. */
  onOrdreUpdated?: (updates: Partial<Ordre>) => void;
}) {
  const [plan, setPlan] = useState<ServiceBoxInterventionsPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<ServiceBoxPushInterventionsResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    serviceboxSyncApi
      .getInterventionsPlan(ordre.id)
      .then((p) => {
        if (cancelled) return;
        setPlan(p);
        if (!p.success) setError(p.error || "Lecture du RDV ServiceBox impossible");
        // Nothing is pre-ticked, on purpose. Pre-ticking "what ServiceBox does not
        // visibly have" shipped a duplicate on its first real use (22/09/2026): the label
        // match could not see that "DISQUES PLAQUETTES AR A REMPLACER" and ServiceBox's
        // "REMPLACEMENT DES DISQUES ET PLAQUETTES DE FREIN ARRIÈRE" are the same work,
        // so it proposed the line and the proposal was taken at face value. A suggestion
        // that arrives already ticked is not a suggestion.
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e?.message || "Erreur"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ordre.id]);

  const candidates: CarvisWorkLine[] = [...(plan?.to_add || []), ...(plan?.already_on_servicebox || [])];
  const chosen = candidates.filter((l) => selected[l.id]);
  // Only a line ServiceBox does not have yet can be wiped by Alpha's answer: ticking lines
  // already there must not wait for the OR (30/09/2026).
  const chosenNew = chosen.filter((l) => !(plan?.already_on_servicebox || []).some((a) => a.id === l.id));
  const blockedByAlpha = !!plan?.dms_in_flight && chosenNew.length > 0;

  const [fetchedOr, setFetchedOr] = useState<string | null>(null);
  const [fetchingOr, setFetchingOr] = useState(false);
  const knownOrNumber = fetchedOr || plan?.or_number || ordre.or_number || "";

  // Générer l'OR depuis Carvis : même transfert Alpha que l'envoi automatique, sur le poste
  // Carvis (010) dont la CT part en PDF sur le serveur Alpha — plus d'impression papier
  // (validé le 23/09/2026). Impossible sans dossier ServiceBox (RDV MyO « rdv:<id> »).
  const serviceboxConfig = useDataStore((st) => st.orgData?.servicebox) as any;
  const operatorEmail = useDataStore((st) => st.userData?.infos?.email) || "";
  const sbDossierId = String(ordre.servicebox_dossier_id || "");
  const hasSbDossier = !!sbDossierId && !sbDossierId.startsWith("rdv:");
  const rdvIdForLink = serviceboxRdvId(ordre);
  const alphaJob = useAlphaTransferStore((st) => st.jobs[ordre.id]);
  const alphaRunning = alphaJob?.status === "running" || alphaJob?.status === "waiting";
  // A send reached Alpha and no OR number came back: Alpha may already have made the OR
  // (FP-986-SK, 13/10/2026 — three clicks, three ORs). The button waits until someone has
  // checked in Alpha; only "J'ai vérifié, renvoyer" sends again (force).
  const [unconfirmedAt] = useState<string | null>(ordre.alpha_sent_unconfirmed_at || null);
  const alphaUnconfirmed = !knownOrNumber && !alphaRunning && (!!unconfirmedAt || !!alphaJob?.unconfirmed);
  const generateOr = (dossierId: string = sbDossierId, force = false) => {
    if (!serviceboxConfig?.username || !dossierId || dossierId.startsWith("rdv:")) return;
    const sites = serviceboxConfig.sites || [];
    startAlphaTransfer({
      ordre,
      dossierId,
      creds: {
        username: serviceboxConfig.username || "",
        password: serviceboxConfig.password || "",
        site_code: serviceboxConfig.site_code || sites[0]?.pdv_id || "",
        operator_email: operatorEmail,
      } as any,
      poste: alphaPosteFor(ordre),
      rdvId: rdvIdForLink || undefined,
      force,
      onTransferred: (updates) => {
        if (updates.or_number) setFetchedOr(updates.or_number);
      },
    });
  };
  // Sans dossier, on ouvre le planning : la fiche RDV seule (modifierRdv.action) n'est
  // qu'un fragment que ServiceBox charge dans sa popup — ouverte à part, elle s'affiche
  // sans mise en page et « Enregistrer » ne fait rien (Titouan, TEMP-C8BDEC, 16/10/2026).
  // ServiceBox n'a pas de lien direct vers un RDV : on dit lequel ouvrir.
  const openInServiceBox = () => {
    const url = hasSbDossier
      ? `https://servicebox.mpsa.com/panier/panierSetCurrent.do?id=${sbDossierId}`
      : "https://servicebox.mpsa.com/agenda/planningReceptionnaire.action?jbnRedirect=true";
    window.open(url, "_blank");
  };
  const receptionLabel = ordre.reception_date?.seconds
    ? new Date(ordre.reception_date.seconds * 1000).toLocaleString("fr-FR", {
        weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "";
  // What was asked for has been carried out — the button has nothing left to do and must
  // not invite a second, duplicating, click.
  const [done, setDone] = useState(false);

  /** « Récupérer le numéro d'OR », c'est-à-dire l'obtenir : lit d'abord l'OR sur le RDV
   *  (déjà transféré depuis ServiceBox), sinon transfère le dossier vers Alpha, qui crée
   *  l'OR (poste Carvis, CT sans papier) — le numéro revient tout seul. Sans dossier
   *  ServiceBox sur le RDV, il n'y a rien à transférer : on le dit. */
  const fetchOrNumber = async () => {
    setFetchingOr(true);
    setError(null);
    try {
      const res = await serviceboxSyncApi.refreshOrNumber(ordre.id);
      if (!res.success) {
        setError(res.error || "Lecture du numéro d'OR impossible");
        return;
      }
      if (res.or_number) {
        setFetchedOr(res.or_number);
        onOrdreUpdated?.({ or_number: res.or_number, num: `OR-${res.or_number}` });
        showNotification({ type: "success", message: `Numéro d'OR récupéré : OR-${res.or_number}` });
        return;
      }
      const dossierId = res.dossier_id || (hasSbDossier ? sbDossierId : "");
      if (dossierId && alphaUnconfirmed) {
        setError(ALPHA_UNCONFIRMED_MESSAGE);
      } else if (dossierId) {
        generateOr(dossierId);
        showNotification({ type: "success", message: "Transfert vers Alpha lancé : le numéro d'OR arrive dans un instant." });
      } else {
        setError(res.detail || "Ce rendez-vous n'a pas encore de dossier dans ServiceBox.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Erreur");
    } finally {
      setFetchingOr(false);
    }
  };

  /** Everything that was asked for, in the only order that works: lines first — Alpha
   *  never receives a line added to a dossier whose OR already exists — then the OR. */
  const handleUpdate = async () => {
    setSending(true);
    setError(null);
    try {
      if (chosen.length > 0) {
        const res = await serviceboxSyncApi.pushInterventions(ordre.id, chosen.map((l) => l.id));
        setResult(res);
        if (!res.success) {
          setError(res.error || "ServiceBox n'a pas enregistré la modification");
          return; // Creating the OR on a basket we failed to complete would seal the gap in.
        }
        showNotification({
          type: "default",
          message: res.unchanged
            ? "ServiceBox avait déjà ces interventions"
            : `${res.added?.length || 0} intervention(s) ajoutée(s) sur ServiceBox`,
        });
      }
      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const nothingToDo = chosen.length === 0;

  // Alpha answers in 15-25 s, then ServiceBox copies the numéro d'OR onto the RDV a few
  // minutes later — and only then can a line be pushed without Alpha's answer wiping it.
  // Telling the user to come back and retry by hand is asking them to poll on our behalf:
  // the modal watches instead, and unlocks itself when the OR lands.
  useEffect(() => {
    if (!plan?.dms_in_flight || result?.success) return;
    const timer = setInterval(() => {
      serviceboxSyncApi
        .getInterventionsPlan(ordre.id)
        .then((p) => setPlan(p))
        .catch(() => {});
    }, 20000);
    return () => clearInterval(timer);
  }, [plan?.dms_in_flight, result?.success, ordre.id]);

  return (
    <BaseModal
      onClose={onClose}
      title="Mettre à jour ServiceBox"
      startIcon={<Icon1 name="sett-servicebox" size={20} />}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-4 p-4">
        {loading ? (
          <div className="flex flex-row items-center gap-3 p-4">
            <div className="w-5 h-5 rounded-full border-[3px] border-neutral-200 border-t-neutral-700 animate-spin" />
            <div className="text-h3 text-neutral-500">Lecture du rendez-vous sur ServiceBox…</div>
          </div>
        ) : (
          <>
            {plan?.rdv_id && (
              <div className="text-h4 text-neutral-500">
                RDV ServiceBox n°{plan.rdv_id}
                {knownOrNumber ? ` — OR n°${knownOrNumber}` : " — pas encore de numéro d'OR"}
              </div>
            )}

            {/* Slots and desks: no button, and saying nothing would read as "not sent". */}
            {plan?.slots && (
              <div className="text-h4 text-neutral-500">
                {plan.slots.auto
                  ? plan.slots.pending
                    ? "Créneaux et guichets : une modification Carvis part vers ServiceBox dans les prochaines minutes."
                    : "Créneaux et guichets : synchronisés automatiquement, rien à envoyer à la main."
                  : "Créneaux et guichets : ServiceBox fait foi pour cette organisation, une modification Carvis n'y est pas écrite."}
              </div>
            )}

            {/* What ServiceBox holds today. Read-only on purpose: this push only adds. */}
            <div>
              <div className="text-h3 font-medium mb-1">Déjà sur ServiceBox</div>
              {plan?.servicebox_lines?.length ? (
                <ul className="flex flex-col gap-1">
                  {plan.servicebox_lines.map((l, i) => (
                    <li key={`${l.nom}-${i}`} className="flex flex-row justify-between gap-3 text-h3 text-neutral-600">
                      <span>{l.nom}</span>
                      <span className="text-neutral-400 shrink-0">{l.duree} h</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-h3 text-neutral-400">Aucune ligne de travail sur ce RDV.</div>
              )}
            </div>

            {/* What Carvis can send. Everything is listed, including what looks like a
                match, because the two sides word the same work differently. */}
            {candidates.length > 0 && (
              <div>
                <div className="text-h3 font-medium mb-1">Interventions Carvis à reporter</div>
                <div className="text-h4 text-neutral-500 mb-1">
                  Comparez avec la liste ci-dessus avant de cocher : les deux côtés ne nomment
                  pas toujours le même travail de la même façon.
                </div>
                <div className="flex flex-col gap-1">
                  {candidates.map((l) => {
                    const alreadyThere = (plan?.already_on_servicebox || []).some((a) => a.id === l.id);
                    return (
                      <label key={l.id} className="flex flex-row items-start gap-2 text-h3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!selected[l.id]}
                          disabled={sending || !!result?.success}
                          onChange={(e) => setSelected((p) => ({ ...p, [l.id]: e.target.checked }))}
                        />
                        <span className={alreadyThere ? "text-neutral-400" : ""}>
                          {l.nom}
                          {l.duree ? <span className="text-neutral-400"> — {l.duree} h</span> : null}
                          {l.forfait_code ? <span className="text-neutral-400"> — forfait {l.forfait_code}</span> : null}
                          {alreadyThere && <span className="text-neutral-400"> (déjà sur ServiceBox)</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Numéro d'OR — lu, jamais créé : Alpha imprime la CT chiffrée dès qu'il crée
                un OR, donc ce transfert reste le geste du garage, fait depuis ServiceBox. */}
            {plan?.success && !knownOrNumber && (
              <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-h3 flex flex-col gap-2">
                <div className="font-medium">Numéro d'OR</div>
                <div className="text-neutral-600">
                  {hasSbDossier ? (
                    <>
                      Ce rendez-vous n'a pas encore de numéro d'OR. « Récupérer le numéro d'OR » transfère le
                      dossier vers Alpha (CT sans impression papier) et le numéro revient ici.
                    </>
                  ) : (
                    <>
                      Ce rendez-vous n'a pas encore de <strong>dossier</strong> dans ServiceBox : sans dossier,
                      Alpha ne peut pas créer d'OR. « Ouvrir dans ServiceBox » ouvre le planning : ouvrez-y
                      le RDV{receptionLabel ? <> du <strong>{receptionLabel}</strong></> : null}
                      {ordre.name ? ` (${ordre.name})` : ""} et cliquez Enregistrer, ServiceBox crée le
                      dossier. Ensuite « Récupérer le numéro d'OR » fait le reste.
                    </>
                  )}
                </div>
                {alphaUnconfirmed && (
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                    Le dossier est déjà parti vers Alpha
                    {unconfirmedAt ? ` (le ${new Date(unconfirmedAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })})` : ""}
                    , mais aucun numéro d'OR n'est revenu. {ALPHA_UNCONFIRMED_MESSAGE} Si l'OR y est,
                    saisissez son numéro sur l'ordre ; ne renvoyez que s'il n'existe pas.
                  </div>
                )}
                <div className="flex flex-row flex-wrap gap-2">
                  <button
                    onClick={fetchOrNumber}
                    disabled={fetchingOr || sending || alphaRunning || alphaUnconfirmed}
                    className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-black text-white hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    {alphaRunning ? "Création de l'OR dans Alpha…" : fetchingOr ? "Lecture sur ServiceBox…" : "Récupérer le numéro d'OR"}
                  </button>
                  {alphaUnconfirmed && hasSbDossier && (
                    <button
                      onClick={() => generateOr(sbDossierId, true)}
                      disabled={fetchingOr || sending}
                      className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-white border border-amber-400 text-amber-800 hover:opacity-80 transition-opacity disabled:opacity-50"
                    >
                      J'ai vérifié, renvoyer
                    </button>
                  )}
                  {(hasSbDossier || rdvIdForLink) && (
                    <button
                      onClick={openInServiceBox}
                      className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-white border border-neutral-300 hover:opacity-80 transition-opacity"
                    >
                      Ouvrir dans ServiceBox
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Alpha is still answering the transfer, and its answer rewrites the dossier's
                work lines: a line pushed now disappears a few minutes later (live on RDV
                133673646, 22/09/2026). Said before the push, not after it evaporated. */}
            {blockedByAlpha && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3">
                Alpha est en train de créer l'OR de cet ordre : son numéro n'est pas encore
                sur le rendez-vous ServiceBox. Une intervention ajoutée maintenant serait
                effacée par la réponse d'Alpha. Inutile de réessayer à la main : cette fenêtre
                vérifie toutes les 20 s et se débloquera toute seule (compter quelques minutes).
              </div>
            )}

            {plan?.success && candidates.length === 0 && (
              <div className="text-h3 text-neutral-500">
                Cet ordre n'a aucune intervention « incluse » à reporter.
              </div>
            )}

            {/* The line reaches the ServiceBox rendez-vous, not an OR Alpha already made,
                and a forfait arrives as a Standard line — both verified live, both said. */}
            {[result?.or_warning, result?.forfait_warning].filter(Boolean).map((w) => (
              <div key={w} className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3">
                {w}
              </div>
            ))}

            {result?.success && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-h3">
                {result.unchanged
                  ? result.detail || "ServiceBox avait déjà ces interventions."
                  : `Ajouté sur ServiceBox et vérifié après relecture : ${(result.added || []).join(", ")}.`}
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-h3">
                {error}
                {result?.lost?.length ? (
                  <div className="mt-1 font-medium">
                    À vérifier sur ServiceBox : {result.lost.join(", ")}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}

        <div className="flex flex-row justify-end gap-2">
          <SecondaryButton label={done ? "Fermer" : "Annuler"} onClick={onClose} />
          {!done && (
            <Button
              label="Mettre à jour ServiceBox"
              loading={sending}
              disabled={loading || sending || nothingToDo || !plan?.success || blockedByAlpha}
              onClick={handleUpdate}
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}
